import { z } from "zod";

import type { UIOperationDescriptor } from "../types";
import { defineUIOperation } from "../types";

/**
 * Input schema for `spansFilter.set`. Ported from the hand-rolled
 * `parseSetSpansFilterInput` in `app/src/agent/tools/spansFilter/parsers.ts`
 * and the server JSON schema in
 * `src/phoenix/server/agents/capabilities/tools/external/set_spans_filter.py`.
 * Strict to match the server schema's `additionalProperties: false`.
 */
const setSpansFilterInputSchema = z.strictObject({
  condition: z
    .string()
    .describe(
      "The span filter DSL expression to apply, including any root-span " +
        "restriction (`parent_id is None`). Pass an empty string to clear " +
        "the filter and show every span."
    ),
});

export type SetSpansFilterOperationInput = z.infer<
  typeof setSpansFilterInputSchema
>;

/**
 * The catalog entry replacing the `set_spans_filter` client-action tool. The
 * description (including the full span filter DSL reference) moves here
 * verbatim from the Python `DESCRIPTION`.
 */
export const setSpansFilterOperation = defineUIOperation({
  name: "spansFilter.set",
  description:
    "Set the spans table filter. The filter is applied declaratively as a " +
    "complete state: the freeform `condition` (Phoenix span filter DSL) " +
    "describes the entire view, and the table is updated to exactly that " +
    "state. There is no notion of leaving part of the filter unchanged — " +
    "always send the whole condition. Pass an empty string to clear it." +
    "\n\n" +
    "ROOT SPANS ARE PART OF THE CONDITION: restrict to root spans -- spans " +
    "with no parent -- with the predicate `parent_id is None`; omit it to " +
    "search every span. This is usually the top-level span of each trace, " +
    "but nothing enforces one root per trace: a fragmented or partially " +
    "ingested trace can have several, so do not treat the row count as a " +
    "trace count. Do NOT restrict to root spans when narrowing to a " +
    "`span_kind` other than CHAIN or AGENT (i.e. LLM, TOOL, RETRIEVER, " +
    "EMBEDDING, RERANKER, EVALUATOR, GUARDRAIL, DECISION) — those kinds are almost " +
    "always nested under a CHAIN/AGENT root, so combining them with a root " +
    "predicate yields zero results even when matching spans exist. The same " +
    "applies to anything that targets nested spans (specific tool names, " +
    "status_code == 'ERROR' on inner calls, annotations attached to leaf " +
    "spans). Trace annotations apply to every span in the annotated trace: " +
    "combine a trace-annotation condition with `parent_id is None` to show " +
    "only root spans from matching traces, or omit the root predicate to " +
    "inspect every span belonging to those traces. The spans table starts " +
    "at `parent_id is None`, so dropping that predicate is how you widen " +
    "the view to every span." +
    "\n\n" +
    "DSL FIELDS:\n" +
    "  - Root spans: `parent_id is None` matches spans with no parent " +
    "pointer, and is the table's default. `parent_span is None` is the " +
    "wider form that also matches orphans -- spans whose parent was never " +
    "ingested -- and is what to use when the user asks about orphans.\n" +
    "  - String: `span_kind`, `name` (the span name, NOT span_name), " +
    "`status_code`, `status_message`, `span_id`, `trace_id`, `parent_id`. " +
    "Compare with `==`, `!=`, or membership via `in`.\n" +
    "  - Numeric: `latency_ms`, " +
    "`cumulative_token_count.{prompt,completion,total}`, " +
    "`llm.token_count.{prompt,completion,total}`. Compare with `<`, " +
    "`<=`, `>`, `>=`.\n" +
    "  - Datetime: `start_time`, `end_time`.\n" +
    "  - Attribute access: `attributes['key.path']`, `input.value`, " +
    "`output.value`, `metadata['key']`.\n" +
    "  - Annotations: `annotations['Name'].label`, " +
    "`annotations['Name'].score`.\n" +
    "  - Substring search: `'needle' in input.value` (any string field, " +
    "ignores case).\n" +
    "  - Cost: `total_cost`, `prompt_cost`, `completion_cost`. These are " +
    "bare names and read this span's own cost row. There is no token name " +
    "here and no cost-per-token name -- for a rate, divide two reductions " +
    "over `cost_details`, e.g. `sum(d.cost for d in cost_details) / " +
    "sum(d.tokens for d in cost_details) > 0.0001`, which yields null " +
    "rather than an error when tokens are zero. For a span's token counts " +
    "use `cumulative_llm_token_count_total` (this span and its " +
    "descendants) or `attributes['llm']['token_count']['total']` (this " +
    "span alone). `attributes['total_cost']` is the separate, unrelated " +
    "spelling for a span attribute of that name.\n" +
    "  - Per-token-type cost rows: `cost_details`, iterated with " +
    "`any`/`all`/`len`/`sum`/`max`/`min`, e.g. " +
    "`any(cost_detail.token_type == 'cache_read' for cost_detail in " +
    "cost_details)`. Element fields: `token_type`, `is_prompt`, `cost`, " +
    "`tokens`, `cost_per_token`.\n" +
    "  - Combine clauses with `and`, `or`, `not`." +
    "\n\n" +
    "VALUE CONVENTIONS:\n" +
    "  - `span_kind` values are UPPERCASE string literals: 'LLM', " +
    "'TOOL', 'CHAIN', 'AGENT', 'RETRIEVER', 'EMBEDDING', 'RERANKER', " +
    "'EVALUATOR', 'GUARDRAIL', 'DECISION', 'UNKNOWN'. Lowercase ('llm') will not " +
    "match.\n" +
    "  - `status_code` values are UPPERCASE: 'OK', 'ERROR', 'UNSET'.\n" +
    "  - Always wrap string literals in single or double quotes." +
    "\n\n" +
    "EXAMPLES BY INTENT:\n" +
    "  - 'Show me LLM spans' → `span_kind == 'LLM'`\n" +
    "  - 'Tool calls' → `span_kind == 'TOOL'`\n" +
    "  - 'Errored spans' → `status_code == 'ERROR'`\n" +
    "  - 'Slow LLM calls' → `span_kind == 'LLM' and latency_ms >= 5000`\n" +
    "  - 'Top-level traces only' → `parent_id is None`\n" +
    "  - 'Slow traces' → `parent_id is None and latency_ms >= 5000`\n" +
    "  - 'Include orphaned spans as roots' → `parent_span is None`\n" +
    "  - 'Hallucinations' → " +
    "`annotations['Hallucination'].label == 'hallucinated'`\n" +
    "  - 'Expensive spans' → `total_cost > 0.1`\n" +
    "  - 'Expensive LLM calls' → " +
    "`span_kind == 'LLM' and total_cost > 0.1`\n" +
    "  - 'Spans that hit the prompt cache' → " +
    "`any(cost_detail.token_type == 'cache_read' for cost_detail in " +
    "cost_details)`\n" +
    "  - 'Spans mentioning agent in input' → `'agent' in input.value`\n" +
    "  - 'Show everything' → `''`\n" +
    "  - 'Reset to default view' → `parent_id is None`.",
  inputSchema: setSpansFilterInputSchema,
  operationKind: "write",
  defaultSuccessOutput: "Spans filter updated.",
  availability: {
    routeHint: "a project's spans/traces view",
  },
});

/** All spans-filter operations, for catalog assembly. */
export const spansFilterOperations: UIOperationDescriptor[] = [
  setSpansFilterOperation,
];
